import { useState, useEffect, useRef } from 'react';
import api from '../api';
import { useLang } from '../i18n/LanguageContext';

const PRESETS = [
  { key: 'cyan',    label: 'Cyan',    accent: '#4fd6e0', accent2: '#2aa6c7' },
  { key: 'gold',    label: 'Gold',    accent: '#f0c25a', accent2: '#e2a23a' },
  { key: 'emerald', label: 'Yashil',  accent: '#34f5a8', accent2: '#10b981' },
  { key: 'magenta', label: 'Pushti',  accent: '#ff5ea0', accent2: '#d6428a' },
  { key: 'violet',  label: 'Binafsha',accent: '#9b8cff', accent2: '#6d5ce0' },
  { key: 'coral',   label: 'Coral',   accent: '#ff7a5c', accent2: '#ff4d6d' },
  { key: 'ice',     label: 'Muz',     accent: '#bcd6ff', accent2: '#7fa8e8' },
];

const DEFAULT = {
  preset: 'cyan', accent: '#4fd6e0', accent2: '#2aa6c7',
  orb1: '#22d3ee', orb2: '#3a7ad0', orb3: '#7c4fd0',
  bg: '#080812', style: 'classic', bg_image: '',
};

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1,3),16);
  const g = parseInt(hex.slice(3,5),16);
  const b = parseInt(hex.slice(5,7),16);
  return `${r},${g},${b}`;
}

function darken(hex, pct = 0.2) {
  let r = parseInt(hex.slice(1,3),16);
  let g = parseInt(hex.slice(3,5),16);
  let b = parseInt(hex.slice(5,7),16);
  r = Math.max(0, Math.round(r*(1-pct)));
  g = Math.max(0, Math.round(g*(1-pct)));
  b = Math.max(0, Math.round(b*(1-pct)));
  return '#' + [r,g,b].map(x=>x.toString(16).padStart(2,'0')).join('');
}

export default function ThemePage() {
  const { t } = useLang();
  const [labs, setLabs] = useState([]);
  const [labId, setLabId] = useState('');
  const [form, setForm] = useState(DEFAULT);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const bgFileRef = useRef();

  useEffect(() => {
    api.get('/owner/labs').then(r => {
      setLabs(r.data);
      if (r.data.length) setLabId(localStorage.getItem('selected_lab_id') || String(r.data[0].id));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!labId) return;
    api.get('/theme').then(r => {
      const found = r.data.find(x => String(x.lab_id) === labId);
      if (found) setForm({ ...DEFAULT, ...found });
      else setForm({ ...DEFAULT, lab_id: Number(labId) });
    }).catch(() => setForm({ ...DEFAULT }));
  }, [labId]);

  function selectPreset(p) {
    const preset = PRESETS.find(x => x.key === p.key);
    if (!preset) return;
    const orb1 = preset.accent;
    const orb2 = darken(preset.accent, 0.3);
    const orb3 = darken(preset.accent2, 0.1);
    setForm(f => ({ ...f, preset: p.key, accent: preset.accent, accent2: preset.accent2, orb1, orb2, orb3 }));
  }

  function setCustomAccent(hex) {
    const accent2 = darken(hex, 0.2);
    const orb1 = hex;
    const orb2 = darken(hex, 0.35);
    const orb3 = darken(accent2, 0.1);
    setForm(f => ({ ...f, preset: 'custom', accent: hex, accent2, orb1, orb2, orb3 }));
  }

  async function uploadBgImage(file) {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('bg_image', file);
      const res = await api.post('/theme/upload-bg', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, bg_image: res.data.url }));
    } catch (e) {
      alert('Rasm yuklanmadi: ' + (e.response?.data?.error || e.message));
    }
    setUploading(false);
  }

  async function save() {
    if (!labId) return;
    setSaving(true);
    try {
      await api.put('/theme', { ...form, lab_id: Number(labId) });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      alert(t('error') + ': ' + (e.response?.data?.error || e.message));
    }
    setSaving(false);
  }

  const { accent, accent2, orb1, orb2, orb3, bg, style, preset } = form;

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>{t('themeTitle')}</h1>
          <p style={{ color: 'var(--text3)', fontSize: 13, marginTop: 4 }}>{t('themeSubtitle')}</p>
        </div>
        {labs.length > 1 && (
          <select value={labId} onChange={e => { setLabId(e.target.value); localStorage.setItem('selected_lab_id', e.target.value); }}
            style={{ background: 'var(--card2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 14px', borderRadius: 8, fontSize: 13 }}>
            {labs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {/* Sol: sozlamalar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Preset ranglar */}
          <div style={{ background: 'var(--card)', borderRadius: 12, padding: 20, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)', marginBottom: 16 }}>{t('presetColors')}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
              {PRESETS.map(p => (
                <button key={p.key} onClick={() => selectPreset(p)} style={{
                  background: `linear-gradient(135deg, ${p.accent}, ${p.accent2})`,
                  border: preset === p.key ? '2px solid #fff' : '2px solid transparent',
                  borderRadius: 10, padding: '14px 8px', cursor: 'pointer',
                  color: '#fff', fontSize: 11, fontWeight: 600,
                  boxShadow: preset === p.key ? `0 0 0 2px ${p.accent}` : 'none',
                  transition: 'all 0.2s',
                }}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Custom rang */}
          <div style={{ background: 'var(--card)', borderRadius: 12, padding: 20, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)', marginBottom: 16 }}>{t('customColor')}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input type="color" value={accent} onChange={e => setCustomAccent(e.target.value)}
                style={{ width: 48, height: 48, border: 'none', background: 'none', cursor: 'pointer', borderRadius: 8 }} />
              <div>
                <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600 }}>{t('mainColorLabel')}</div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
                  Accent: <span style={{ color: accent, fontFamily: 'monospace' }}>{accent}</span>
                  {' → '}
                  <span style={{ color: accent2, fontFamily: 'monospace' }}>{accent2}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Fon rangi */}
          <div style={{ background: 'var(--card)', borderRadius: 12, padding: 20, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)', marginBottom: 16 }}>{t('bgColorLabel')}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input type="color" value={bg} onChange={e => setForm(f => ({ ...f, bg: e.target.value }))}
                style={{ width: 48, height: 48, border: 'none', background: 'none', cursor: 'pointer', borderRadius: 8 }} />
              <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                {t('bgLabel')} <span style={{ fontFamily: 'monospace', color: 'var(--text)' }}>{bg}</span>
              </div>
            </div>
          </div>

          {/* Fon rasmi */}
          <div style={{ background: 'var(--card)', borderRadius: 12, padding: 20, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)', marginBottom: 12 }}>Fon rasmi (ixtiyoriy)</div>
            <input ref={bgFileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
              style={{ display: 'none' }} onChange={e => uploadBgImage(e.target.files[0])} />
            {form.bg_image ? (
              <div style={{ borderRadius: 10, overflow: 'hidden', height: 120, position: 'relative', background: '#000' }}>
                <img src={form.bg_image.startsWith('/') ? `${process.env.REACT_APP_API_URL?.replace('/api','') || 'http://localhost:3000'}${form.bg_image}` : form.bg_image}
                  alt="fon" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  onError={e => { e.target.style.display = 'none'; }} />
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'rgba(0,0,0,0.4)' }}>
                  <button onClick={() => { bgFileRef.current.value=''; bgFileRef.current.click(); }} disabled={uploading}
                    style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.9)', color: '#000', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                    {uploading ? 'Yuklanmoqda...' : '📷 Almashtirish'}
                  </button>
                  <button onClick={() => setForm(f => ({ ...f, bg_image: '' }))}
                    style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: 'rgba(239,68,68,0.85)', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                    ✕ O'chirish
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => { bgFileRef.current.value=''; bgFileRef.current.click(); }} disabled={uploading}
                style={{ width: '100%', padding: '18px', borderRadius: 10, border: '2px dashed var(--border)', background: 'transparent', color: uploading ? 'var(--text3)' : 'var(--text2)', cursor: uploading ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}>
                {uploading ? '⏳ Yuklanmoqda...' : '📁 Rasm tanlang (JPG, PNG, WEBP)'}
              </button>
            )}
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8 }}>
              Rasm qo'yilsa, dizayn foniga o'rnatiladi (qoramtir qatlam bilan). Maks: 10MB
            </div>
          </div>

          {/* Dizayn uslubi */}
          <div style={{ background: 'var(--card)', borderRadius: 12, padding: 20, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)', marginBottom: 16 }}>{t('designStyle')}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {/* Classic */}
              <button onClick={() => setForm(f => ({ ...f, style: 'classic' }))} style={{
                borderRadius: 12, cursor: 'pointer', overflow: 'hidden', padding: 0,
                border: `2px solid ${style === 'classic' ? accent : 'var(--border)'}`,
                background: 'transparent', transition: 'all 0.2s',
                boxShadow: style === 'classic' ? `0 0 0 2px ${accent}40` : 'none',
              }}>
                <div style={{ background: '#080812', padding: '12px 10px' }}>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                    {['#1a1a35','#1a1a35','#1a1a35'].map((c,i) => (
                      <div key={i} style={{ flex: 1, height: 36, background: c, borderRadius: 6, border: '1px solid #2a2a50' }} />
                    ))}
                  </div>
                  <div style={{ height: 6, background: '#2a2a50', borderRadius: 3, width: '70%' }} />
                </div>
                <div style={{
                  padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  background: style === 'classic' ? `rgba(${hexToRgb(accent)},0.1)` : 'var(--card2)',
                }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: style === 'classic' ? accent : 'var(--text)', textAlign: 'left' }}>{t('classicLabel')}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'left' }}>{t('classicDesc')}</div>
                  </div>
                  {style === 'classic' && <div style={{ fontSize: 16, color: accent }}>✓</div>}
                </div>
              </button>

              {/* Liquid Glass */}
              <button onClick={() => setForm(f => ({ ...f, style: 'glass' }))} style={{
                borderRadius: 12, cursor: 'pointer', overflow: 'hidden', padding: 0,
                border: `2px solid ${style === 'glass' ? accent : 'var(--border)'}`,
                background: 'transparent', transition: 'all 0.2s',
                boxShadow: style === 'glass' ? `0 0 0 2px ${accent}40` : 'none',
              }}>
                <div style={{ background: bg, padding: '12px 10px', position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(circle at 30% 30%, ${orb1}50, transparent 60%), radial-gradient(circle at 80% 80%, ${orb2}40, transparent 60%)`, opacity: 0.5 }} />
                  <div style={{ display: 'flex', gap: 6, marginBottom: 8, position: 'relative' }}>
                    {[0,1,2].map(i => (
                      <div key={i} style={{ flex: 1, height: 36, background: 'rgba(255,255,255,0.08)', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)' }} />
                    ))}
                  </div>
                  <div style={{ height: 6, background: 'rgba(255,255,255,0.12)', borderRadius: 3, width: '70%', position: 'relative' }} />
                </div>
                <div style={{
                  padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  background: style === 'glass' ? `rgba(${hexToRgb(accent)},0.1)` : 'var(--card2)',
                }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: style === 'glass' ? accent : 'var(--text)', textAlign: 'left' }}>Liquid Glass</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'left' }}>{t('glassDesc')}</div>
                  </div>
                  {style === 'glass' && <div style={{ fontSize: 16, color: accent }}>✓</div>}
                </div>
              </button>
            </div>
          </div>

          {/* Saqlash */}
          <button onClick={save} disabled={saving || !labId} style={{
            padding: '14px 28px', borderRadius: 12, border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
            background: saved ? 'linear-gradient(135deg, #00ff88, #10b981)'
                               : `linear-gradient(135deg, ${accent}, ${accent2})`,
            color: '#fff', fontSize: 15, fontWeight: 700, opacity: saving ? 0.7 : 1,
            transition: 'all 0.3s',
          }}>
            {saved ? t('themeSaved') : saving ? t('savingLabel') : `💾 ${t('save')}`}
          </button>
        </div>

        {/* O'ng: jonli preview */}
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)', marginBottom: 12 }}>{t('livePreview')}</div>
          <ShellPreview accent={accent} accent2={accent2} orb1={orb1} orb2={orb2} orb3={orb3} bg={bg} style={style} />
        </div>
      </div>
    </div>
  );
}

function ShellPreview({ accent, accent2, orb1, orb2, orb3, bg, style: themStyle }) {
  const isGlass = themStyle === 'glass';

  const cardBg = isGlass
    ? 'rgba(255,255,255,0.05)'
    : `color-mix(in srgb, ${bg} 70%, ${accent} 30%)`;

  const fakGames = [
    { name: 'CS2', cat: 'FPS' },
    { name: 'Dota 2', cat: 'MOBA' },
    { name: 'FIFA 25', cat: 'Sport' },
  ];

  return (
    <div style={{
      borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)',
      background: bg, position: 'relative', aspectRatio: '16/9', minHeight: 320,
    }}>
      {/* Fon orblar */}
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', width: 200, height: 200, borderRadius: '50%', filter: 'blur(60px)', opacity: 0.25, background: orb1, top: '-50px', left: '20%' }} />
        <div style={{ position: 'absolute', width: 150, height: 150, borderRadius: '50%', filter: 'blur(50px)', opacity: 0.2,  background: orb2, bottom: '10px', right: '10%' }} />
        <div style={{ position: 'absolute', width: 120, height: 120, borderRadius: '50%', filter: 'blur(40px)', opacity: 0.15, background: orb3, top: '40%', left: '5%' }} />
      </div>

      {/* Header */}
      <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
        <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 14, fontWeight: 900,
          background: `linear-gradient(135deg, ${accent}, ${accent2})`,
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          ⚡ Defuse
        </div>
        <div style={{ fontSize: 11, color: accent, fontFamily: 'monospace' }}>NightOwl Gaming</div>
      </div>

      {/* O'yin kartalari */}
      <div style={{ padding: '8px 16px', display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, position: 'relative', zIndex: 1 }}>
        {fakGames.map((g, i) => (
          <div key={i} style={{
            borderRadius: 10, padding: '14px 10px', textAlign: 'center',
            background: isGlass ? 'rgba(255,255,255,0.06)' : cardBg,
            border: `1px solid ${isGlass ? 'rgba(255,255,255,0.12)' : `rgba(${hexToRgb(accent)},0.2)`}`,
            backdropFilter: isGlass ? 'blur(12px)' : 'none',
          }}>
            <div style={{ fontSize: 22, marginBottom: 6 }}>🎮</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#fff', marginBottom: 2 }}>{g.name}</div>
            <div style={{ fontSize: 9, color: accent, fontWeight: 600 }}>{g.cat}</div>
          </div>
        ))}
      </div>

      {/* O'ynash tugmasi */}
      <div style={{ padding: '10px 16px', position: 'relative', zIndex: 1 }}>
        <div style={{
          background: `linear-gradient(135deg, ${accent}, ${accent2})`,
          borderRadius: 8, padding: '8px 16px', display: 'inline-block',
          fontSize: 12, fontWeight: 700, color: '#fff',
        }}>▶ O'ynash</div>
        <span style={{ fontSize: 11, color: isGlass ? 'rgba(255,255,255,0.6)' : '#8892b0', marginLeft: 10 }}>
          Uslub: {themStyle === 'glass' ? '🪟 Glass' : '⬜ Klassik'}
        </span>
      </div>
    </div>
  );
}
